import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './voiceSummary.module.css';

/*
 * "Listen to trip summary": reads a short summary of the trip aloud with the
 * device's own voice (Web Speech API — free, no backend). The sentence being
 * read is shown underneath so people can follow along.
 *
 *   <VoiceSummary lines={['First sentence.', 'Second.']} label='Listen to trip summary' />
 *   <VoiceSummary scripts={{ en: [...], kn: [...] }} />   // English / ಕನ್ನಡ switch
 *
 * The ಕನ್ನಡ switch only shows on devices that have a Kannada voice (most
 * Android phones; rarely laptops / iPhone). Hidden entirely on browsers
 * without speech support.
 */

const LABELS = { en: 'Listen to trip summary', kn: 'ಪ್ರವಾಸದ ಸಾರಾಂಶ ಕೇಳಿ' };
// short visible text next to the speaker icon, so people know what it does
const SHORT_LABELS = { en: 'Audio summary', kn: 'ಧ್ವನಿ ಸಾರಾಂಶ' };
const LANG_KEY = 'trvto-voice-lang';
// reading speed: × the voice's normal rate (remembered on this device)
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const SPEED_KEY = 'trvto-voice-speed';
const DEFAULT_SPEED = 1.25;
const readSpeed = () => {
    try {
        const saved = Number(localStorage.getItem(SPEED_KEY));
        return SPEEDS.includes(saved) ? saved : DEFAULT_SPEED;
    } catch {
        return DEFAULT_SPEED;
    }
};
const speedLabel = (s) => `${s}×`;
const readLang = () => {
    try {
        return localStorage.getItem(LANG_KEY) === 'kn' ? 'kn' : 'en';
    } catch {
        return 'en';
    }
};
// Android's Google voice engine can speak Kannada even when the browser
// doesn't list a Kannada voice (it's picked by lang = kn-IN)
const isAndroid = () => typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');
const hasKannadaVoice = () => (window.speechSynthesis.getVoices() || []).some((v) => /^kn/i.test(v.lang));

const supported = () => typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

// best voice on this device: Indian English first, then any English
function pickVoice(lang = 'en') {
    const voices = window.speechSynthesis.getVoices() || [];
    const by = (fn) => voices.find(fn);
    if (lang === 'kn') return by((v) => /^kn/i.test(v.lang) && /google|natural|neural/i.test(v.name)) || by((v) => /^kn/i.test(v.lang)) || null;
    return (
        by((v) => v.lang === 'en-IN' && /google|natural|neural/i.test(v.name)) ||
        by((v) => v.lang === 'en-IN') ||
        by((v) => /^en/i.test(v.lang) && /google|natural|neural|samantha/i.test(v.name)) ||
        by((v) => /^en/i.test(v.lang)) ||
        null
    );
}

const SpeakerIcon = () => (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
        <path d='M4 9v6h4l5 4V5L8 9H4z' fill='currentColor' />
        <path d='M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
    </svg>
);
const PauseIcon = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
        <rect x='6' y='5' width='4' height='14' rx='1' fill='currentColor' />
        <rect x='14' y='5' width='4' height='14' rx='1' fill='currentColor' />
    </svg>
);
const PlayIcon = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M7 5v14l12-7z' fill='currentColor' />
    </svg>
);
const CloseIcon = () => (
    <svg width='14' height='14' viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' />
    </svg>
);
const StopIcon = () => (
    <svg width='14' height='14' viewBox='0 0 24 24' aria-hidden='true'>
        <rect x='5' y='5' width='14' height='14' rx='2' fill='currentColor' />
    </svg>
);

export default function VoiceSummary({ lines, scripts, label, onFinished, onIndexChange, variant = 'chip' }) {
    const [state, setState] = useState('idle'); // idle | playing | paused
    const [index, setIndex] = useState(0);
    // how many words of the current sentence have been spoken (they appear one by one)
    const [shown, setShown] = useState(0);
    const wordTimer = useRef(null);
    const pausedRef = useRef(false);
    const captionRef = useRef(null);

    // long sentences: keep the word being read in view (the caption shows 3 lines)
    useEffect(() => {
        const box = captionRef.current;
        const word = box?.querySelector('[data-saying="1"]');
        if (!box || !word) {
            if (box) box.scrollTop = 0;
            return;
        }
        const line = parseFloat(getComputedStyle(box).lineHeight) || 20;
        box.scrollTop = Math.max(0, word.offsetTop - line); // caption is the offsetParent
    }, [shown, index]);
    const [knReady, setKnReady] = useState(false);
    const [lang, setLang] = useState(readLang);
    const [speed, setSpeed] = useState(readSpeed);
    const speedRef = useRef(speed);
    const stopped = useRef(true);
    const restart = useRef(false);
    const id = useRef(Math.random().toString(36).slice(2));
    const voice = useRef(null);
    const hasKn = Boolean(scripts?.kn?.length);
    // Kannada can be spoken here: a Kannada voice is listed, or it's Android
    const knPlayable = knReady || isAndroid();
    const useLang = hasKn && lang === 'kn' && knPlayable ? 'kn' : 'en';
    const source = scripts ? scripts[useLang] || scripts.en : lines;
    const text = useMemo(() => (source || []).filter(Boolean), [source]);

    useEffect(() => {
        if (!supported()) return undefined;
        const load = () => {
            setKnReady(hasKannadaVoice());
            voice.current = pickVoice(useLang);
        };
        load();
        window.speechSynthesis.addEventListener?.('voiceschanged', load);
        return () => window.speechSynthesis.removeEventListener?.('voiceschanged', load);
    }, [useLang]);

    // leaving the page must not keep talking
    useEffect(
        () => () => {
            if (!supported()) return;
            stopped.current = true;
            clearTimeout(wordTimer.current);
            window.speechSynthesis.cancel();
        },
        []
    );

    // a different trip: stop what was being read
    useEffect(() => {
        if (!supported()) return;
        stopped.current = true;
        clearTimeout(wordTimer.current);
        window.speechSynthesis.cancel();
        setState('idle');
        setIndex(0);
        if (restart.current) {
            restart.current = false;
            play();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    // only one player reads at a time: another one starting stops this one
    useEffect(() => {
        const onOther = (e) => {
            if (e.detail === id.current || stopped.current) return;
            stopped.current = true;
            clearTimeout(wordTimer.current);
            pausedRef.current = false;
            setState('idle');
            setIndex(0);
            onIndexChange?.(-1);
        };
        window.addEventListener('trvto-voice-start', onOther);
        return () => window.removeEventListener('trvto-voice-start', onOther);
    }, [onIndexChange]);

    if (!supported() || text.length === 0) return null;

    const clearWordTimer = () => {
        clearTimeout(wordTimer.current);
        wordTimer.current = null;
    };

    /*
     * Words appear as they are spoken. Browsers that report word positions
     * (onboundary) drive it exactly; others (many Android voices) don't, so a
     * timer paced to the speaking rate reveals them until a boundary arrives.
     */
    const revealByTimer = (words, rate, from = 0) => {
        clearWordTimer();
        let n = from;
        const tick = () => {
            if (pausedRef.current) {
                wordTimer.current = setTimeout(tick, 150);
                return;
            }
            n += 1;
            setShown((prev) => Math.max(prev, Math.min(n, words.length)));
            if (n < words.length) {
                const len = words[n - 1]?.length || 4;
                wordTimer.current = setTimeout(tick, Math.max(200, 110 + len * 55) / rate);
            }
        };
        tick();
    };

    const speakFrom = (i) => {
        clearWordTimer();
        if (stopped.current || i >= text.length) {
            setState('idle');
            setIndex(0);
            onIndexChange?.(-1);
            if (i >= text.length) onFinished?.();
            return;
        }
        setIndex(i);
        setShown(0);
        onIndexChange?.(i);
        const sentence = text[i];
        const words = sentence.split(/\s+/).filter(Boolean);
        let exact = false; // got word positions from the browser
        const u = new SpeechSynthesisUtterance(sentence);
        try {
            if (voice.current) u.voice = voice.current;
        } catch {
            // some browsers reject the voice object — the lang below still picks one
        }
        u.lang = voice.current?.lang || (useLang === 'kn' ? 'kn-IN' : 'en-IN');
        // Kannada voices sound rushed sooner: a slightly lower base rate
        u.rate = (useLang === 'kn' ? 0.95 : 1) * speedRef.current;
        u.onstart = () => {
            if (!exact) revealByTimer(words, u.rate);
        };
        u.onboundary = (e) => {
            if (e.name && e.name !== 'word') return;
            exact = true;
            clearWordTimer();
            const spokenBefore = sentence.slice(0, e.charIndex).split(/\s+/).filter(Boolean).length;
            setShown(Math.min(words.length, spokenBefore + 1));
        };
        u.onend = () => {
            clearWordTimer();
            setShown(words.length);
            speakFrom(i + 1);
        };
        u.onerror = (e) => {
            // "interrupted"/"canceled" = we stopped it on purpose
            if (e?.error !== 'interrupted' && e?.error !== 'canceled') speakFrom(i + 1);
        };
        window.speechSynthesis.speak(u);
    };

    const play = () => {
        pausedRef.current = false;
        window.dispatchEvent(new CustomEvent('trvto-voice-start', { detail: id.current }));
        window.speechSynthesis.cancel();
        stopped.current = false;
        setState('playing');
        speakFrom(0);
    };
    const pause = () => {
        pausedRef.current = true;
        window.speechSynthesis.pause();
        setState('paused');
    };
    const resume = () => {
        pausedRef.current = false;
        window.speechSynthesis.resume();
        setState('playing');
    };
    // faster / slower: the sentence being read starts again at the new speed
    const changeSpeed = (step) => {
        const at = SPEEDS.indexOf(speedRef.current);
        const next = SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, (at < 0 ? 1 : at) + step))];
        if (next === speedRef.current) return;
        speedRef.current = next;
        setSpeed(next);
        try {
            localStorage.setItem(SPEED_KEY, String(next));
        } catch {
            // private mode: just don't remember it
        }
        if (state === 'playing') {
            window.speechSynthesis.cancel();
            speakFrom(index);
        }
    };

    const stop = () => {
        clearWordTimer();
        pausedRef.current = false;
        stopped.current = true;
        window.speechSynthesis.cancel();
        setState('idle');
        setIndex(0);
        onIndexChange?.(-1);
    };

    const switchLang = (next) => {
        if (next === useLang) return;
        restart.current = true; // keep reading, in the other language
        setLang(next);
        try {
            localStorage.setItem(LANG_KEY, next);
        } catch {
            // private mode: just don't remember it
        }
    };

    const trigger =
        variant === 'icon' ? (
            <button
                type='button'
                className={`${styles.iconBtn} ${state !== 'idle' ? styles.iconOn : ''}`}
                onClick={state === 'idle' ? play : stop}
                aria-label={state === 'idle' ? label || LABELS[useLang] : 'Stop reading'}
                title={state === 'idle' ? label || LABELS[useLang] : 'Stop reading'}
            >
                {state === 'idle' ? <SpeakerIcon /> : <StopIcon />}
                <span className={styles.iconText}>{state === 'idle' ? SHORT_LABELS[useLang] : 'Stop'}</span>
            </button>
        ) : (
            <button
                type='button'
                className={`${styles.chip} ${state !== 'idle' ? styles.iconOn : ''}`}
                onClick={state === 'idle' ? play : stop}
            >
                {state === 'idle' ? <SpeakerIcon /> : <StopIcon />}
                {state === 'idle' ? label || LABELS[useLang] : 'Stop'}
            </button>
        );

    // while reading: a small player floating above the booking bar, so the page doesn't move
    const player =
        state !== 'idle' &&
        createPortal(
            <div className={styles.float} role='region' aria-label='Audio summary'>
                <div className={styles.floatTop}>
                    <span className={`${styles.wave} ${state === 'playing' ? styles.live : ''}`} aria-hidden='true'>
                        <i />
                        <i />
                        <i />
                    </span>
                    <button
                        type='button'
                        className={styles.ctrl}
                        onClick={state === 'playing' ? pause : resume}
                        aria-label={state === 'playing' ? 'Pause' : 'Resume'}
                    >
                        {state === 'playing' ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <span className={styles.count}>
                        {index + 1}/{text.length}
                    </span>
                    <div className={styles.speed} role='group' aria-label='Reading speed'>
                        <button
                            type='button'
                            onClick={() => changeSpeed(-1)}
                            disabled={speed === SPEEDS[0]}
                            aria-label='Slower'
                            title='Slower'
                        >
                            −
                        </button>
                        <span aria-live='polite'>{speedLabel(speed)}</span>
                        <button
                            type='button'
                            onClick={() => changeSpeed(1)}
                            disabled={speed === SPEEDS[SPEEDS.length - 1]}
                            aria-label='Faster'
                            title='Faster'
                        >
                            +
                        </button>
                    </div>
                    {hasKn && (
                        <div className={styles.langs} role='group' aria-label='Voice language'>
                            {[
                                ['en', 'EN'],
                                ['kn', 'ಕನ್ನಡ']
                            ].map(([code, name]) => (
                                <button
                                    key={code}
                                    type='button'
                                    lang={code}
                                    className={`${styles.lang} ${useLang === code ? styles.langOn : ''}`}
                                    aria-pressed={useLang === code}
                                    disabled={code === 'kn' && !knPlayable}
                                    title={code === 'kn' && !knPlayable ? 'Kannada voice is not available on this device' : undefined}
                                    onClick={() => switchLang(code)}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    )}
                    <button type='button' className={styles.close} onClick={stop} aria-label='Close'>
                        <CloseIcon />
                    </button>
                </div>
                <p ref={captionRef} className={styles.caption} lang={useLang} aria-label={text[index]}>
                    {/* words not spoken yet keep their space (no jumping), shown as they're read */}
                    {(text[index] || '')
                        .split(/\s+/)
                        .filter(Boolean)
                        .map((word, w) => (
                            <span
                                key={`${index}-${w}`}
                                aria-hidden='true'
                                data-saying={w === shown - 1 ? '1' : undefined}
                                className={w < shown - 1 ? styles.said : w === shown - 1 ? styles.saying : styles.notYet}
                            >
                                {word}{' '}
                            </span>
                        ))}
                </p>
            </div>,
            document.body
        );

    return (
        <>
            {trigger}
            {player}
        </>
    );
}
